import Buttons from '../Buttons/Buttons';
import style from './Calendar.module.css';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import '../../styles/CalendarPicker.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Russian } from 'flatpickr/dist/l10n/ru';
import { calculateEstimate, formatDurationWords, formatMoney, getBookingTimeExample, getOffsetFromMoscow, getOffsetFromMoscowFormatted } from '../../utils/utils';
import { Accordion, AccordionDetails, AccordionSummary, Slider } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/config';
import { useSnackbarAlert } from '../../providers/SnackbarProvider';

export default function Calendar({
    date,
    setDate,
    orderLoading,
    startSlot,
    setStartSlot,
    durationMinutes,
    setDurationMinutes,
    sliderValue,
    setSliderValue,
    sliderTime,
    setSliderTime,
    orders,
    slots, setSlots,
    price, setPrice,
    setPriceList,
    kinoNight, setKinoNight,
    slotInterval,
    bufferTimeMs,
    people,
    filial,
    hall,
    countPeople,
    etcLoading,
    changePage
}) {
    const snackbar = useSnackbarAlert();
    const calendarRef = useRef(null);
    const flatpickrInstance = useRef(null);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [reserved, setReserved] = useState(false);
    const [loadingPrice, setLoadingPrice] = useState(false);
    const priceUpdateTimeoutRef = useRef(null);
    const lastRequestRef = useRef({ startSlot: null, durationMinutes: null });
    const timeZoneOffset = new Date().getTimezoneOffset();

    useEffect(() => {
        flatpickrInstance.current = flatpickr(calendarRef.current, {
            locale: Russian,
            inline: true,
            static: true,
            defaultDate: date,
            minDate: new Date(Date.now() + 3 * 60 * 60 * 1000),
            maxDate: new Date().setMonth(new Date().getMonth() + 1),
            onChange: (selectedDates) => {
                setDate(selectedDates[0]);
            }
        });

        return () => {
            if (flatpickrInstance.current) {
                flatpickrInstance.current.destroy();
            }
        };
    }, []);

    useEffect(() => {
        if (orders === null) return;
        setSlots(prevSlots => {
            const selectionEnd = startSlot + durationMinutes * 60 * 1000;
            const now = Date.now();
            const disableUntil = now + 3 * 60 * 60 * 1000;

            const updatedSlots = prevSlots.map(slot => {
                const slotStart = slot.start;
                const slotEnd = slot.start + slotInterval;

                // занято ли по заказам
                const taken = isSlotTaken(slotStart, slotEnd);

                // попадает ли в диапазон выбора
                const inSelectedRange = slotStart >= startSlot && slotEnd <= selectionEnd;

                const isReserved = inSelectedRange && taken;
                const isActive = inSelectedRange;

                // слоты буфера
                const isBuffered = !isReserved && !isActive && 
                    orders.some(order => {
                        const bufferedStart = order.start - bufferTimeMs;
                        const bufferedEnd = order.end + bufferTimeMs;
                        return slotStart >= bufferedStart && slotEnd <= bufferedEnd && 
                            (slotEnd <= order.start || slotStart >= order.end);
                    });

                const isTooEarly = slotStart < disableUntil;

                return {
                    ...slot,
                    isReserved,
                    isActive,
                    isBuffered,
                    isTaken: taken || isTooEarly
                };
            });

            updateTimeText(selectionEnd);
            return updatedSlots;
        });
    }, [startSlot, durationMinutes, slotInterval, orders]);

    useEffect(() => {
        if (orders && !orderLoading) scrollTo("text_info_start");
    }, [orderLoading]);

    useEffect(() => {
        setDurationMinutes(parseInt(sliderValue) || 60);
    }, [sliderValue]);

    useEffect(() => {
        let isValidate = true;

        if (!startSlot) isValidate = false;
        if (slots.filter(slot => slot.isReserved === true).length !== 0) {
            isValidate = false;
            setReserved(true);
        } else setReserved(false);
        if (!price) isValidate = false;

        setButtonDisabled(!isValidate);
    }, [startSlot, slots, price]);

    // ВАЖНО: Этот эффект остался для расчета Кино-ночи
    useEffect(() => {
        if (!startSlot) return;
        
        const hours = durationMinutes / 60;
        const start = (new Date(startSlot).getTime() / 1000);
        const end = start + (3600 * hours);

        const startTime = new Date(start*1000);
        const endTime = new Date(end*1000);
        
        // Расчет Кино-ночи (этот расчет остался локальным)
        if (startTime.getHours() === 0 && startTime.getMinutes() === 0 && endTime.getHours() === 9 && endTime.getMinutes() === 0) {
            if (filial === 2 && hall === 6) {
                if (people >= 11) setKinoNight(18000);
                else setKinoNight(15000);
            } else {
                if (people >= 5) setKinoNight(8500);
                else if (filial === 1) {
                    if (hall === 1 || hall === 2) setKinoNight(7500);
                    else if (hall === 3 || hall === 4 || hall === 5 || hall === 6) setKinoNight(7000);
                } else if (filial === 2) {
                    setKinoNight(7000);
                }
            }
        } else {
            setKinoNight(0);
        }
    }, [startSlot, durationMinutes, filial, hall, people]);

    function updateTimeText(end) {
        if (!startSlot || !end) return;

        const startStr = new Date(startSlot).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        const endStr = new Date(end).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

        setSliderTime(`${startStr} - ${endStr}`);
    }

    function isSlotTaken(slotStart, slotEnd) {
        if (!orders) return false;
        return orders.some(order => {
            const bufferedStart = order.start - bufferTimeMs;
            const bufferedEnd = order.end + bufferTimeMs;
            return !(slotEnd <= bufferedStart || slotStart >= bufferedEnd);
        });
    }

    const handleSlotClick = (slot) => {
        if (isSlotTaken(slot.start, slot.end)) return;
        if (startSlot === null) scrollTo("text_info_slider");
        setStartSlot(slot.start);
    };

    const click = () => {
        if (!buttonDisabled) changePage(5);
    }

    function scrollTo(element_id) {
        const element = document.getElementById(element_id);
        let scrollSleep = 0;
        if (!element) scrollSleep = 100;

        if (scrollSleep) {
            setTimeout(() => {
                const element = document.getElementById(element_id);
                element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, scrollSleep);
        } else {
            element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    // Функция для получения цены с сервера - УБИРАЕМ useCallback для упрощения
    const fetchPriceFromServer = async () => {
        if (!startSlot) {
            setPrice(0);
            setPriceList(null);
            return;
        }
        
        // Проверяем, не запрашиваем ли мы уже те же данные
        const currentRequestKey = `${startSlot}-${durationMinutes}-${filial}-${hall}-${countPeople}`;
        if (lastRequestRef.current.key === currentRequestKey && loadingPrice) {
            return; // Пропускаем дублирующий запрос
        }
        
        lastRequestRef.current.key = currentRequestKey;
        
        setLoadingPrice(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/index.php`, {
                type: 'getPrice',
                filial: filial,
                hall: hall,
                countPeople: countPeople,
                startTime: Math.floor(startSlot / 1000),
                endTime: Math.floor((startSlot + durationMinutes * 60 * 1000) / 1000)
            }, {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true
            });
            
            if (res?.data.status === "success") {
                const result = res?.data.price;
                
                if (!result || !Array.isArray(result) || result.length === 0) {
                    throw new Error('Некорректные данные о цене');
                }

                let totalPrice = 0;
                result.forEach(priceItem => {
                    totalPrice += priceItem.rate * priceItem.hours;
                });
                
                setPrice(totalPrice);
                setPriceList(result);
            } else {
                throw new Error(res?.data.error || 'Ошибка сервера');
            }
        } catch (err) {
            snackbar.error(err.response?.data?.error || err.message || "Ошибка загрузки цены.");
        } finally {
            setLoadingPrice(false);
        }
    };

    // ОСНОВНОЙ ЭФФЕКТ для обновления цены - только при изменении ключевых параметров
    useEffect(() => {
        // Очищаем предыдущий таймаут
        if (priceUpdateTimeoutRef.current) {
            clearTimeout(priceUpdateTimeoutRef.current);
        }
        
        // Если нет startSlot, сбрасываем цену
        if (!startSlot) {
            setPrice(0);
            setPriceList(null);
            return;
        }
        
        // Устанавливаем новый таймаут для запроса
        priceUpdateTimeoutRef.current = setTimeout(() => {
            fetchPriceFromServer();
        }, 500);
        
        return () => {
            if (priceUpdateTimeoutRef.current) {
                clearTimeout(priceUpdateTimeoutRef.current);
            }
        };
    }, [startSlot, durationMinutes, filial, hall, countPeople]); // Только эти зависимости

    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    // Обработчик для гарантированного обновления при отпускании слайдера
    const handleSliderCommit = () => {
        if (startSlot) {
            // Очищаем таймаут дебаунса и делаем запрос сразу
            if (priceUpdateTimeoutRef.current) {
                clearTimeout(priceUpdateTimeoutRef.current);
            }
            fetchPriceFromServer();
        }
    };

    const isNoMSKTZ = timeZoneOffset !== -180;

    return (
        <>
            <div className={style.calendar_container}>
                {!date && 
                    <div className={style.text_info}>Выберите день Вашего бронирования, нажав на нужную дату.</div>
                }
                <div ref={calendarRef} className={style.calendarFullWidth}></div>
            </div>
            {orders ? (
                <div className={style.container} id="time_container">
                    {!startSlot ? (
                        <div className={style.text_info} id="text_info_start">Выберите время начала Вашего бронирования, нажав на свободный слот.</div>
                    ) : (
                        <>
                            <div className={style.text_info} id="text_info_slider">
                                <p>Выберите длительность Вашего бронирования, передвигая слайдер (ползунок).</p>
                                <p>Минимальное время — 2 часа.</p>
                            </div>
                            <div className={style.price_block}>
                                <div className={style.time_value}>{sliderTime}</div>
                                <div className={`${style.price} ${kinoNight !== 0 && style.kinoNight}`}>
                                    {loadingPrice ? (
                                        <span className={style.loading_price}>Загрузка...</span>
                                    ) : price > 0 ? (
                                        <>
                                            {formatMoney(price)} ₽
                                        </>
                                    ) : (
                                        <span className={style.no_price}>—</span>
                                    )}
                                </div>
                                {kinoNight !== 0 && (
                                    <div className={style.kinoNight_block}>
                                        <div className={style.price}>{formatMoney(kinoNight)} ₽</div>
                                        <div className={style.kinoNight_text}>тариф "Кино-ночь"</div>
                                    </div>
                                )}
                            </div>
                            <Slider
                                min={120}
                                max={600}
                                step={30}
                                value={sliderValue}
                                onChange={handleSliderChange}
                                onChangeCommitted={handleSliderCommit}
                                disabled={!startSlot || loadingPrice}
                                valueLabelDisplay="on"
                                valueLabelFormat={formatDurationWords}
                                className={style.slider}
                            />
                        </>
                    )}
                    {reserved && (
                        <div className={style.text_reserved}>Вы выбрали время, которое уже занято.</div>
                    )}
                    {isNoMSKTZ && (
                        <div className={style.no_msk}>
                            <p>Мы обнаружили у Вас часовой пояс, отличающийся от московского ({getOffsetFromMoscowFormatted()}).</p>
                            <p>Пожалуйста, учитывайте это при выборе времени Вашей брони.</p>
                            <p>Бронирование происходит по Вашему часовому поясу.</p>
                            <p>Пример: Вы хотите забронировать зал с 10:00 до 13:00 по московскому времени.</p>
                            <p>{getBookingTimeExample()}</p>
                        </div>
                    )}
                    <div className={style.time_container}>
                        {slots.map((slot, index) => (
                            <button
                                key={index}
                                className={`${style.slot} 
                                    ${slot.isTaken ? style.deactive : ''}
                                    ${slot.isNextDay ? style.next_day_slot : ''}
                                    ${slot.isActive ? style.active : ''}
                                    ${slot.isReserved ? style.reserved : ''}
                                    ${slot.isBuffered ? style.deactive : ''}
                                `}
                                disabled={slot.isTaken}
                                data-timestamp={slot.start}
                                onClick={
                                    !slot.isTaken && !slot.isNextDay && !slot.isReserved && !slot.isBuffered 
                                        ? () => handleSlotClick(slot) 
                                        : () => {}
                                }
                            >
                                {slot.timeString}
                            </button>
                        ))}
                    </div>
                    <div className={style.accordion_block}>
                        <Accordion slotProps={{ heading: { component: 'h4' } }}>
                            <AccordionSummary
                                expandIcon={<KeyboardArrowDownIcon />}
                            >
                                <div className={style.accordion_title}>Что за квадратики?</div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <div className={style.info_container}>
                                    <div className={style.info_block}>
                                        <div className={`${style.slot}`}>00:00</div>
                                        <div className={style.info_block_text}>Свободное время</div>
                                    </div>
                                    <div className={style.info_block}>
                                        <div className={`${style.slot} ${style.active}`}>00:00</div>
                                        <div className={style.info_block_text}>Выбранное Вами время</div>
                                    </div>
                                    <div className={style.info_block}>
                                        <div className={`${style.slot} ${style.deactive}`}>00:00</div>
                                        <div className={style.info_block_text}>Занятое время</div>
                                    </div>
                                    <div className={style.info_block}>
                                        <div className={`${style.slot} ${style.next_day_slot}`}>00:00</div>
                                        <div className={style.info_block_text}>Время следующего дня</div>
                                    </div>
                                    <div className={style.info_block}>
                                        <div className={`${style.slot} ${style.reserved}`}>00:00</div>
                                        <div className={style.info_block_text}>Выбранное Вами время, которое уже занято</div>
                                    </div>
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </div>
            ) : orderLoading ? (
                <div className={style.loading_container}>
                    <img src="/assets/img/logo3.gif" alt="Загрузка" className={style.gif} />
                </div>
            ) : ""}
            <Buttons 
                button1={{ 
                    text: "Далее", 
                    onClick: () => click(), 
                    disabled: buttonDisabled || loadingPrice, 
                    loading: orderLoading || loadingPrice 
                }} 
                button2={{ text: "Назад", onClick: () => changePage(3) }} 
            />
        </>
    )
}